
import { type CommonProps, useObservability, useObservabilityManager } from "@qamf/observability";
import { loadStripe } from "@stripe/stripe-js";

import { PaymentsAppInitQueryParam, PaymentsAppReadyEvent } from "./interfaces/environment";
import { type CommandMessage, type IPaymentCurrency, type IPriceFormatResult, type IProviderData, type ResultPayload, type Settings } from "./interfaces/interfaces";
import { ERROR_TYPE, ERRORS_TO_HANDLE, GENERIC_ERROR_TYPE, OPERATION_ID, PAYMENT_FORM_ID, STRIPE_ERROR, STRIPE_PAYMENT_ERROR } from "./interfaces/labels";
import type { StripePaymentError } from "./interfaces/payment-error";
import { AppInsightsEvent } from "./observability-events";



async function settingsResolver() {
	const contextFileUrl = __LAUNCH_MODE__ === "Standard"
		? "/context.json"
		: `/settings/${__ENVIRONMENT__}-${__CHANNEL__}.json`;
	const response = await fetch(contextFileUrl, { method: "GET", cache: "no-cache" }).then((data) => data.json());
	return response as Settings;
}

function priceFormatter(amount: number, options: IPaymentCurrency): IPriceFormatResult {
	const decimalSep = options.DecimalSep ?? ".";
	const decimalsDigitsOpt = options.DecDigits ? Math.abs(options.DecDigits) : 0;
	const decimalsDigits = isNaN(decimalsDigitsOpt) ? 0 : decimalsDigitsOpt;
	const thousandsSep = options.ThousandSep ?? "";
	const sign = amount < 0 ? "-" : "";
	const amountParsed = Math.abs(Number(amount) || 0).toFixed(decimalsDigits);
	const integerPart = parseInt(amountParsed);
	const integerPartString = integerPart.toString();
	const hasThousands = integerPartString.length > 3 ? integerPartString.length % 3 : 0;
	const hasDecimals = decimalsDigits > 0;
	const composition = {
		symbolBefore:
			options?.Symbol && options.SymbolBeforeValue ? options.Symbol : "",
		sign,
		thousands: hasThousands ? integerPartString.substr(0, hasThousands) : "",
		thousandsSeparator: hasThousands ? thousandsSep : "",
		integers: integerPartString
			.substr(hasThousands)
			.replace(/(\d{3})(?=\d)/g, "$1" + thousandsSep),
		decimals: hasDecimals
			? Math.abs(amount - integerPart)
				.toFixed(decimalsDigits)
				.split(".")[1]
			: "",
		decimalsSeparator: hasDecimals ? decimalSep : "",
		symbolAfter:
			options?.Symbol && !options.SymbolBeforeValue ? options.Symbol : ""
	};
	const result = (composition.symbolBefore ? composition.symbolBefore + " " : "")
		+ composition.sign
		+ (composition.thousands ? composition.thousands + composition.thousandsSeparator : "")
		+ composition.integers
		+ composition.decimalsSeparator
		+ composition.decimals
		+ (composition.symbolAfter ? " " + composition.symbolAfter : "");
	return { composition, result };
}


function redirectWithParams(code: string, message: string, operationId?: string) {
	const Url = new URL(window.location.href);

	const params = new URLSearchParams();
	params.set(code, message);
	if (operationId)
		params.set("operationId", operationId);
	Url.search = params.toString();
	location.href = Url.toString();
}

export function showOverlay() {
	const loadingContent = document.getElementsByClassName("payment")[0];
	const formPayment = document.getElementById(PAYMENT_FORM_ID);
	if (loadingContent instanceof HTMLElement && formPayment) {
		loadingContent.style.display = "block";
		formPayment.style.display = "none";
	}
}
export function hideOverlay() {
	const loadingContent = document.getElementsByClassName("payment")[0];
	const formPayment = document.getElementById(PAYMENT_FORM_ID);
	if (loadingContent instanceof HTMLElement && formPayment) {
		loadingContent.style.display = "none";
		formPayment.style.display = "flex";
	}
}


function onClosePopup() {
	window.close();
}

export async function stripeForm(paymentData: Extract<CommandMessage, { command: "stripe-payment" }>["payload"], targetElementId: string): Promise<void | StripePaymentError> {
	const contentHTML = `<form id=${PAYMENT_FORM_ID}>
						<div class="form-group">
							<div id="form-header">
								<img src="images/stripe_logo.png" alt="StripeLogo" height="90">
							</div>
							<div id="stripe-div"></div>
							<button type="submit" id="payment-button">
								<div
									id="spinner"
									class="spinner hidden"
								></div>
								<span id="button-text">PAY</span>
							</button>
							<div
								id="error-message"
							></div>
							<div
							<div id="close-button-container">
								<button id="close-button">Cancel Payment</button>
							</div>
						</div>
					</form>
					<div class="splash-screen payment" style="display: none">
						<span class="splash-content">
							<div class="splash-spinner">
								<div class="bounce1"></div>
								<div class="bounce2"></div>
								<div class="bounce3"></div>
							</div>
						</span>
					</div>`;
	document.body.innerHTML = contentHTML;

	document.getElementById("close-button")?.addEventListener("click", onClosePopup);

	const stripe = await loadStripe(paymentData.data.PublicKey, { stripeAccount: paymentData.data.StripeUserId });
	if (!stripe)
		return "load-sdk";

	const { result } = priceFormatter(paymentData.amount, paymentData.currency);
	const button = document.getElementById("button-text");

	if (!button)
		return "button-pay-error";

	button.innerText = `PAY ${result}`;

	const elements = stripe.elements({
		clientSecret: paymentData.data.ClientSecret,
		appearance: {
			theme: "stripe",
			variables: {
				colorPrimary: "#47a2a6",
				focusBoxShadow: "#47a2a6",
				iconColor: "#47a2a6",
				tabIconSelectedColor: "#47a2a6"
			}
		}
	});

	if (!elements)
		return "load-elements";

	const paymentElement = elements.create("payment");

	paymentElement.on("loaderror", function(event) {
		redirectWithParams(STRIPE_ERROR, "create-elements", paymentData.operationId);
	});

	paymentElement.mount(`#${targetElementId}`);

	const form = document.getElementById(PAYMENT_FORM_ID);
	if (!form)
		return "form-undefined";

	//FIXME: [lib-payments] Implement Dynamic Language Switching in the Stripe Form: #68470

	form.addEventListener("submit", async(event) => {
		event.preventDefault();
		const {trackEvent} = useObservability();

		trackEvent(AppInsightsEvent.SubmittedStripeForm, {
			timestamp: new Date().toISOString(),
			operationId: paymentData.operationId
		});


		showOverlay();

		await elements.submit();

		const returnUrl = window.location.href.split("?")[0];
		const { error: confirmError } = await stripe!.confirmPayment({
			elements,
			confirmParams: {
				return_url: returnUrl,
			},
			clientSecret: paymentData.data.ClientSecret,
		});

		if (confirmError) {
			trackEvent(AppInsightsEvent.SubmittedStripeFormFailed, {
				timestamp: new Date().toISOString(),
				errorMessage: confirmError.message ?? "No error message",
				operationId: paymentData.operationId
			});

			hideOverlay();
			const messageContainer = document.querySelector("#error-message");
			if (messageContainer instanceof HTMLElement) {
				messageContainer.textContent = "";
				if (confirmError.code && ERRORS_TO_HANDLE.includes(confirmError.code)) {
					messageContainer.textContent = confirmError.message ?? "";
					messageContainer.style.color = "red";
				}
			}
		}

	});
}

function redirectForm(
	url: string,
	method: "GET" | "POST" | "PUT" | "PATCH" | "get" | "post" | "put" | "patch" = "POST",
	data?: object
) {
	if (method.toLowerCase() === "get" && !data)
		return location.replace(url);

	const form = document.createElement("form");
	form.style.display = "none";
	form.setAttribute("action", url);
	form.setAttribute("method", method.toLowerCase());
	let inputs = "";
	if (data) {
		Object.keys(data).forEach(field => {
			const value = (data as any)[field];
			inputs += `<input type="hidden" name="${field}" value="${value}" />`;
		});
		form.innerHTML = inputs;
	}
	document.body.appendChild(form);
	form.submit();
}

window.addEventListener("message", async(e) => {
	const messageData = e.data as CommandMessage;
	if (messageData?.payload?.operationId)
		sessionStorage.setItem(OPERATION_ID, messageData.payload.operationId);

	const {trackEvent} = useObservability();

	if (messageData.command === "redirect") {

		trackEvent(AppInsightsEvent.RedirectEvent, {
			payload: messageData.payload,
			operationId: messageData.payload.operationId
		});

		const { url, method, data } = (e.data as any).payload;
		redirectForm(url, method, data);
	} else if (messageData.command === "stripe-payment") {

		trackEvent(AppInsightsEvent.StripeEvent,{
			payload: messageData.payload,
			operationId: messageData.payload.operationId
		});


		const formResult = await stripeForm(messageData.payload, "stripe-div");
		if (formResult)
			redirectWithParams(STRIPE_ERROR, formResult, messageData.payload.operationId);
	}
});

window.addEventListener("load", async() => {
	const settings = await settingsResolver();
	if (!settings.appInsightsConnString) return;

	const { setupAppInsights, setCommonPropsResolver, setEventsPrefix } = useObservabilityManager();

	setupAppInsights({
		config: {
			connectionString: settings.appInsightsConnString,
			disableExceptionTracking: true,
			disableTelemetry: __LAUNCH_MODE__ === "DevelopSession"
		}
	});

	const rawEnvironment = settings.environment ?? __ENVIRONMENT__;
	const rawChannel = settings.channel ?? __CHANNEL__;

	const map = <T extends string, U extends string>(
		v: T,
		m: Record<T, U>
	): U => m[v] ?? (v as unknown as U);

	const Environment = map(rawEnvironment, {
		production: "Production",
		staging: "Staging",
		development: "Development",
		expo: "Expo"
	});

	const Channel = map(rawChannel, {
		stable: "Stable",
		beta:   "Beta",
	});

	setCommonPropsResolver((): CommonProps => ({
		Environment,
		Channel,
		Version: __VERSION__,
		Source: "PaymentApp",
		CenterId:  null,
		CompanyId: null,
		LaunchMode: __LAUNCH_MODE__
	}));

	setEventsPrefix("qubicaamf.payment_app");

	const {trackEvent} = useObservability();

	const isInit = location.href.indexOf(`?${PaymentsAppInitQueryParam}=`) > 0;
	if (isInit) {
		if (window.opener)
			window.opener.postMessage(PaymentsAppReadyEvent, "*");
		else
			window.parent?.postMessage(PaymentsAppReadyEvent, "*");

		trackEvent(AppInsightsEvent.ReadyEvent, { isPopup: Boolean(window.opener) });

	} else {
		const params = new URLSearchParams(window.location.search);
		const parsedParams = Object.fromEntries(params.entries());

		const operationId = params.get("operationId")
            ?? sessionStorage.getItem(OPERATION_ID)
			?? undefined;

		let providerData: IProviderData;
		if (STRIPE_ERROR in parsedParams) {
			providerData = {
				name: ERROR_TYPE,
				code: parsedParams[STRIPE_ERROR],
				message: "Error occurred while initializing Stripe SDK"
			};
		} else if (STRIPE_PAYMENT_ERROR in parsedParams && parsedParams[STRIPE_PAYMENT_ERROR] === "failed") {
			providerData = {
				name: GENERIC_ERROR_TYPE,
				code: parsedParams[STRIPE_PAYMENT_ERROR],
				message: "Error during the payment"
			};
		} else
			providerData = parsedParams;

		const result: ResultPayload = {
			providerReplied: true,
			providerData
		};

		if (window.opener)
			window.opener.postMessage(result, "*");
		else
			window.parent?.postMessage(result, "*");

		trackEvent(AppInsightsEvent.ResultEvent, {
			result: result,
			operationId: operationId
		});

		sessionStorage.removeItem(OPERATION_ID);
	}
});
